import React from 'react';

import * as analytics from 'analytics/Analytics';
import {Sequence} from 'components/Sequence';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {LINEAR_LAYER_FULL} from 'constants/MapsConstants';
import {AaSequenceColorScheme} from 'molbio/aa-sequences/util/AaSequenceColoringConstants';
import {LayersDict, timesPerSlice, whichDisplay} from 'molbio/maps/linear/LayeringHelpers';
import TranslationAminoAcid from 'molbio/maps/linear/TranslationAminoAcid';
import {getDisplayAminoAcids} from 'molbio/maps/linear/TranslationDisplayUtil';
import TranslationNameLabel from 'molbio/maps/linear/TranslationNameLabel';
import Type from 'molbio/maps/Type';
import Translation from 'molbio/translations/backbone/Translation';
import getTestProps from 'test/lib/cypress/getTestProps';

interface TranslationsProps {
  colorScheme: AaSequenceColorScheme;
  sliceTranslations: Array<Translation>;
  sliceStart: number;
  sliceEnd: number;
  aminoAcidIndicesVisible: boolean;
  fontType: Type;
  indexType: Type;
  layersDict: LayersDict;
  layeringMode: typeof LINEAR_LAYER_FULL;
  sequence: Sequence;
  redraw: () => void;
  xFn: (index: number) => number;
  yFn: (index: number) => number;
  openContextMenu: (e: React.MouseEvent, translation: Translation) => void;
}

/**
 * Renders translation features on the sequence map.
 */
export default function TranslationsView({
  aminoAcidIndicesVisible,
  colorScheme,
  layersDict,
  layeringMode,
  sliceStart,
  sliceEnd,
  sliceTranslations,
  sequence,
  xFn,
  yFn,
  indexType,
  fontType,
  openContextMenu,
}: TranslationsProps): JSX.Element {
  const translationSegments = sliceTranslations.flatMap((translation) => {
    const translationSegments = timesPerSlice<Translation>(
      translation,
      sliceStart,
      sliceEnd,
      translation.drawStart(sequence),
      translation.drawEnd(sequence)
    );
    return translationSegments.map((translation, index) => ({translation, segmentIndex: index}));
  });

  function handleClick(e: React.MouseEvent, translation: Translation, start: number, end: number): void {
    const selection = sequence.selection();

    if (e.shiftKey) {
      selection.expandRange(start, end);
      // expandRange will trigger a linearExtend or circularExtend event which removes
      // the translation selection so re-select it afterwards.
      translation.set({selected: true});
    } else {
      selection.select(translation, start, end);
    }
  }

  function selectTranslation(translation: Translation): void {
    sequence.selection().select(translation);
  }

  function openTranslationPanel(translation: Translation): void {
    if (sequence.allowFeatureEdits() && translation.editable()) {
      analytics.track('translation', 'open_translation_panel', {
        product: ANALYTICS_MODULE.MOLBIO,
        openedFrom: 'translation_double_click',
        sequence_id: sequence.get('id'),
      });
      sequence.get('selection').trigger('translation-panel', null, translation);
    }
  }

  return (
    <g>
      {translationSegments.map(({translation, segmentIndex}) => {
        const displayPosition = whichDisplay(
          sliceStart,
          sliceEnd,
          translation.drawStart(sequence),
          translation.drawEnd(sequence),
          segmentIndex,
          sequence.length()
        )!;
        if (displayPosition == null) {
          return;
        }
        const {start, end} = displayPosition;
        const aminoAcids = translation.getAminoAcids(
          sequence,
          Math.max(start, sliceStart),
          Math.min(end, sliceEnd),
          colorScheme
        );
        const displayAminoAcids = getDisplayAminoAcids(aminoAcids, sliceStart, sliceEnd, sequence.length());

        const layerY = yFn(layersDict[translation.get(layeringMode)]);
        return (
          // These classnames have no real effect; keeping temporarily for consistency.
          <g
            key={`${translation.id}-${segmentIndex}`}
            className="layer-group"
            transform={`translate(0, ${layerY})`}
            {...getTestProps({component: 'translationLayeredView', element: 'root'})}
          >
            {displayAminoAcids.map((aa, i) => (
              <TranslationAminoAcid
                key={i}
                aa={aa}
                aminoAcidIndicesVisible={aminoAcidIndicesVisible}
                fontType={fontType}
                indexType={indexType}
                sliceStart={sliceStart}
                sliceEnd={sliceEnd}
                xFn={xFn}
                sequence={sequence}
                translation={translation}
                fontOpacityFactor={1}
                handleClick={(e: React.MouseEvent, start: number, end: number) => {
                  handleClick(e, translation, start, end);
                }}
                handleDoubleClick={() => {
                  selectTranslation(translation);
                }}
                openContextMenu={(e: React.MouseEvent) => {
                  openContextMenu(e, translation);
                }}
              />
            ))}
            <TranslationNameLabel
              aminoAcidIndicesVisible={aminoAcidIndicesVisible}
              fontType={fontType}
              indexType={indexType}
              sequence={sequence}
              translation={translation}
              xFn={xFn}
              displayStart={start}
              displayEnd={end}
              handleClick={() => {
                selectTranslation(translation);
              }}
              handleDoubleClick={() => {
                selectTranslation(translation);
                openTranslationPanel(translation);
              }}
              openContextMenu={(e: React.MouseEvent) => {
                openContextMenu(e, translation);
              }}
            />
          </g>
        );
      })}
    </g>
  );
}
